import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useDispatch } from "react-redux";
import { httpRequest } from "../redux/Action";
import { API_END_POINTS } from "../api/ApiEndPonts";
import { IoCloudUploadOutline, IoFileTrayStackedOutline, IoCheckmarkCircleOutline } from "react-icons/io5";

function ExcelUpload() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("QUESTION_BANK"); // 'QUESTION_BANK' or 'PYQ'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const uploadExcel = () => {
    if (!file) return alert("Please select a file first!");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const json = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      // API Endpoint choose karna based on tab
      const endpoint = activeTab === "QUESTION_BANK" 
        ? API_END_POINTS.bulkUploadQuestions.KEY
        : API_END_POINTS.uploadPYQ.KEY;

         const formData = new FormData();
         formData.append("file", file); 

      dispatch(
        httpRequest({
          key: endpoint,
          data: formData,
          onSuccess: () => {
            alert(`${activeTab} uploaded successfully!`);
            setFile(null);
            setLoading(false);
          },
          onError: () => {
            alert("Upload failed. Check console.");
            setLoading(false);
          },
        })
      );
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Bulk Data Import</h2>
          <p style={styles.subtitle}>Upload your Excel files to populate the database instantly.</p>
        </div>

        {/* Tab Switcher */}
        <div style={styles.tabContainer}>
          <button
            onClick={() => {setActiveTab("QUESTION_BANK"); setFile(null);}}
            style={{ ...styles.tab, ...(activeTab === "QUESTION_BANK" ? styles.activeTab : {}) }}
          >
            <IoFileTrayStackedOutline size={20} /> Question Bank
          </button>
          <button
            onClick={() => {setActiveTab("PYQ"); setFile(null);}}
            style={{ ...styles.tab, ...(activeTab === "PYQ" ? styles.activeTab : {}) }}
          >
            <IoCheckmarkCircleOutline size={20} /> PYQ Series
          </button>
        </div>

        {/* Upload Zone */}
        <div style={styles.uploadBox}>
          <div style={styles.dropZone}>
            <IoCloudUploadOutline size={50} color="#6366f1" />
            <p style={styles.dropText}>
              {file ? `Selected: ${file.name}` : `Drag & drop ${activeTab.replace('_', ' ')} Excel here`}
            </p>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              style={styles.fileInput}
            />
            <button style={styles.browseBtn}>Browse Files</button>
          </div>

          <div style={styles.infoBox}>
            <h4 style={styles.infoTitle}>Note for {activeTab}:</h4>
            <p style={styles.infoText}>
              {activeTab === "QUESTION_BANK" 
                ? "Excel must have columns: subject, chapter, question, optionA, optionB, answer."
                : "Excel must have columns: title, year, subject, question, solution."}
            </p>
          </div>

          <button 
            disabled={loading || !file} 
            onClick={uploadExcel} 
            style={{...styles.uploadBtn, opacity: (loading || !file) ? 0.6 : 1}}
          >
            {loading ? "Processing..." : `Upload to ${activeTab.replace('_', ' ')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "40px 20px",
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  },
  card: {
    width: "100%",
    maxWidth: "600px",
    backgroundColor: "#fff",
    borderRadius: "24px",
    padding: "30px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
  },
  header: { textAlign: "center", marginBottom: "30px" },
  title: { fontSize: "24px", color: "#1e293b", margin: 0 },
  subtitle: { color: "#64748b", fontSize: "14px", marginTop: "5px" },
  tabContainer: {
    display: "flex",
    gap: "10px",
    backgroundColor: "#f1f5f9",
    padding: "6px",
    borderRadius: "14px",
    marginBottom: "25px",
  },
  tab: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "#64748b",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  activeTab: {
    backgroundColor: "#fff",
    color: "#6366f1",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
  uploadBox: { display: "flex", flexDirection: "column", gap: "20px" },
  dropZone: {
    position: "relative",
    border: "2px dashed #e2e8f0",
    borderRadius: "20px",
    padding: "40px 20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "border-color 0.2s",
    backgroundColor: "#fcfcfd",
  },
  fileInput: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    cursor: "pointer",
  },
  dropText: { color: "#475569", fontSize: "15px", marginTop: "15px", fontWeight: "500" },
  browseBtn: {
    marginTop: "10px",
    padding: "8px 18px",
    backgroundColor: "#eef2ff",
    color: "#6366f1",
    border: "none",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "13px",
  },
  infoBox: {
    backgroundColor: "#fffbeb",
    padding: "15px",
    borderRadius: "12px",
    borderLeft: "4px solid #f59e0b",
  },
  infoTitle: { margin: "0 0 5px 0", fontSize: "13px", color: "#92400e" },
  infoText: { margin: 0, fontSize: "12px", color: "#b45309", lineHeight: "1.5" },
  uploadBtn: {
    backgroundColor: "#1e293b",
    color: "#fff",
    border: "none",
    padding: "16px",
    borderRadius: "14px",
    fontWeight: "700",
    fontSize: "16px",
    cursor: "pointer",
    transition: "transform 0.2s",
  },
};

export default ExcelUpload;